import {
    ArrowDownIcon,
    ArrowUpIcon,
    PencilIcon,
    ShareIcon,
    TrashIcon,
    VideoCameraIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import { useEffect, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import Spinner from "../components/layout/Spinner";
import Share from "../components/matches/Share";
import { sendShareNotification } from "../components/utils/emailNotify";
import { usingFirestore } from "../components/utils/firestoreData";
import {
    assignPlaylist,
    fetchAssignmentsForPlaylist,
    unassignPlaylist,
} from "../components/utils/teamPlayerData";
import { fetchAllPlayers, fetchAllTeams } from "../components/utils/teamsPlayers";
import {
    classNames,
    dayTimeCode,
    decodeHtml
} from "../components/utils/Utils";
import { useLanguage } from "../context/Language/LanguageContext";
import {
    sharePlaylist,
    storePlaylist,
} from "../context/VBLiveAPI/VBLiveAPIAction";

function PlaylistAssignmentsPanel({ playlist, t }) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allPlayers, setAllPlayers] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [playerSearch, setPlayerSearch] = useState("");
  const [teamSearch, setTeamSearch] = useState("");

  const reload = async () => {
    if (!playlist?.id) return;
    try {
      setAssignments(await fetchAssignmentsForPlaylist(playlist.id));
    } catch (e) {
      console.log("Failed to reload assignments", e);
    }
  };

  useEffect(() => {
    if (!playlist?.id) return;
    setLoading(true);
    Promise.all([
      fetchAssignmentsForPlaylist(playlist.id),
      fetchAllPlayers(),
      fetchAllTeams(),
    ])
      .then(([asgn, ps, ts]) => {
        setAssignments(asgn);
        setAllPlayers(ps);
        setAllTeams(ts);
        setLoading(false);
      })
      .catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlist?.id]);

  const assignedSet = new Set(assignments.map((a) => `${a.scope}_${a.refId}`));

  const playerTerm = playerSearch.trim().toLowerCase();
  const filteredPlayers = playerTerm
    ? allPlayers
        .filter((p) => !assignedSet.has(`player_${p.id}`))
        .filter((p) =>
          `${p.firstName ?? ""} ${p.lastName ?? ""} ${p.nickName ?? ""}`
            .toLowerCase()
            .includes(playerTerm)
        )
        .slice(0, 6)
    : [];

  const teamTerm = teamSearch.trim().toLowerCase();
  const filteredTeams = teamTerm
    ? allTeams
        .filter((tm) => !assignedSet.has(`team_${tm.id}`))
        .filter((tm) =>
          String(tm.name ?? "").toLowerCase().includes(teamTerm)
        )
        .slice(0, 6)
    : [];

  const doAssign = async (scope, entity) => {
    await assignPlaylist(scope, entity.id, {
      id: playlist.id,
      description: playlist.description ?? "",
      tags: playlist.tags ?? "",
      dateInSeconds: playlist.dateInSeconds ?? null,
    });
    if (scope === "player") setPlayerSearch("");
    else setTeamSearch("");
    await reload();
  };

  const doUnassign = async (a) => {
    await unassignPlaylist(a.id);
    await reload();
  };

  return (
    <div className="mt-4 border-t border-base-300 pt-3">
      <p className="mb-2 text-sm font-semibold">{t("Players & Teams")}</p>
      {loading ? (
        <p className="text-xs text-base-content/60">{t("Loading\u2026")}</p>
      ) : assignments.length === 0 ? (
        <p className="mb-2 text-xs text-base-content/60">
          {t("No players or teams assigned.")}
        </p>
      ) : (
        <div className="mb-2 flex flex-wrap gap-1">
          {assignments.map((a) => (
            <span
              key={a.id}
              className="flex items-center gap-1 rounded bg-base-200 px-2 py-0.5 text-xs"
            >
              <span className="text-base-content/40 font-bold">
                {a.scope === "team" ? "T" : "P"}
              </span>
              {a.refName}
              <button
                className="ml-0.5 text-base-content/40 hover:text-error"
                onClick={() => doUnassign(a)}
                title={t("Remove")}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <p className="mb-0.5 text-xs text-base-content/60">{t("Add player")}</p>
          <input
            type="text"
            className="input-generic"
            placeholder={t("Search players...")}
            value={playerSearch}
            onChange={(e) => setPlayerSearch(e.target.value)}
          />
          {filteredPlayers.length > 0 && (
            <ul className="absolute z-20 mt-1 max-h-40 w-full overflow-y-auto rounded border border-base-300 bg-base-100 shadow">
              {filteredPlayers.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className="w-full px-2 py-1 text-left text-xs hover:bg-base-200"
                    onClick={() => doAssign("player", p)}
                  >
                    {`${p.firstName ?? ""} ${p.lastName ?? ""}`.trim()}
                    {p.nickName ? ` (${p.nickName})` : ""}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="relative flex-1">
          <p className="mb-0.5 text-xs text-base-content/60">{t("Add team")}</p>
          <input
            type="text"
            className="input-generic"
            placeholder={t("Search teams...")}
            value={teamSearch}
            onChange={(e) => setTeamSearch(e.target.value)}
          />
          {filteredTeams.length > 0 && (
            <ul className="absolute z-20 mt-1 max-h-40 w-full overflow-y-auto rounded border border-base-300 bg-base-100 shadow">
              {filteredTeams.map((tm) => (
                <li key={tm.id}>
                  <button
                    type="button"
                    className="w-full px-2 py-1 text-left text-xs hover:bg-base-200"
                    onClick={() => doAssign("team", tm)}
                  >
                    {tm.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function PlaylistsList({ userEmail }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  // const { userEmail } = location.state;
  const [allPlaylists, setAllPlaylists] = useState([]);
  const [filteredPlaylists, setFilteredPlaylists] = useState([]);
  const [allTagsOptions, setAllTagsOptions] = useState([]);
  const [selectedTagOptions, setSelectedTagOptions] = useState([]);
  const [selectedScreen, setSelectedScreen] = useState(0);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [sortColumn, setSortColumn] = useState(0);
  const [sortAscending, setSortAscending] = useState(true);
  const [thisDayTimeCode, setThisDayTimeCode] = useState(dayTimeCode());
  const [loading, setLoading] = useState(true);
  const [, forceUpdate] = useState(0);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPublic, setShowPublic] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [editForm, setEditForm] = useState({ description: "", comments: "", tags: "" });
  const [editSaving, setEditSaving] = useState(false);

  const fetchAllMyPlaylists = async () => {
    setLoading(true);
    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: process.env.REACT_APP_VBLIVE_API_URL.includes("vercel")
        ? process.env.REACT_APP_VBLIVE_API_URL +
          `/Session/GetPlaylistInfoInServerForApp/${userEmail}/VBLive`
        : process.env.REACT_APP_VBLIVE_API_URL +
          `/Session/GetPlaylistInfoInServerForApp?serverName=${userEmail}&appName=VBLive`,
      headers: {},
    };

    var playlists = [];
    await axios
      .request(config)
      .then((response) => {
        playlists = response.data;
        for (var pl of playlists) {
          pl.description = decodeHtml(pl.description);
          pl.comments = decodeHtml(pl.comments);
          pl.tags = decodeHtml(pl.tags);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
      });
    return playlists;
  };

  const fetchAllSharedPlaylists = async () => {
    setLoading(true);
    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: process.env.REACT_APP_VBLIVE_API_URL.includes("vercel")
        ? process.env.REACT_APP_VBLIVE_API_URL +
          `/Session/GetSharedPlaylistsForServer/${userEmail}/VBLive`
        : process.env.REACT_APP_VBLIVE_API_URL +
          `/Session/GetSharedPlaylistsForServer?serverName=${userEmail}&appName=VBLive`,
      headers: {},
    };

    var playlists = [];
    await axios
      .request(config)
      .then((response) => {
        playlists = response.data;
        for (var pl of playlists) {
          pl.description = decodeHtml(pl.description);
          pl.comments = decodeHtml(pl.comments);
          pl.tags = decodeHtml(pl.tags);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
      });
    return playlists;
  };

  const doDeletePlaylist = async (playlist) => {
    confirmAlert({
      title: t("Delete Playlist"),
      message: t("Are you sure you want to delete the selected play list?"),
      buttons: [
        {
          label: t("Yes"),
          onClick: async () => {
            setLoading(true);
            let config = {
              method: "get",
              maxBodyLength: Infinity,
              url: process.env.REACT_APP_VBLIVE_API_URL.includes("vercel")
                ? process.env.REACT_APP_VBLIVE_API_URL +
                  `/Session/DeletePlaylist/${playlist.id}`
                : process.env.REACT_APP_VBLIVE_API_URL +
                  `/Session/DeletePlaylist?sessionId=${playlist.id}`,
              headers: {},
            };

            await axios
              .request(config)
              .then((response) => {
                // console.log(JSON.stringify(response.data));
                setLoading(false);
                doInit();
              })
              .catch((error) => {
                console.log(error);
              });
          },
        },
        {
          label: t("No"),
          onClick: () => {},
        },
      ],
    });
  };

  const doSelect = (sel) => () => {
    for (var m of filteredPlaylists) {
      m.selected = sel;
    }
    forceUpdate((n) => !n);
  };

  const doToggleSelect = (playlist) => {
    playlist.selected = !playlist.selected;
    forceUpdate((n) => !n);
  };

  const onTagChanged = (e) => {
    setSelectedTagOptions(e);
    if (e.length === 0) {
      setSelectedTagOptions([{ value: "All", label: t("All Tags") }]);
      setFilteredPlaylists(allPlaylists);
      forceUpdate((n) => !n);
      return;
    }
    var allsel = e[e.length - 1].value === "All";
    if (allsel) {
      setFilteredPlaylists(allPlaylists);
      setSelectedTagOptions([{ value: "All", label: t("All Tags") }]);
    } else {
      var tagoptions = e.filter((t) => t.value !== "All");
      setSelectedTagOptions(tagoptions);
      var tags = [];
      for (var tg of e) {
        if (tg.value !== "All") {
          tags.push(tg.value);
        }
      }
      var fpls = [];
      for (var pl of allPlaylists) {
        const pltags = pl.tags.split("|");
        for (var pltag of pltags) {
          if (tags.includes(pltag)) {
            fpls.push(pl);
          }
        }
      }
      setFilteredPlaylists(fpls);
    }
    forceUpdate((n) => !n);
  };

  const doSharePlaylist = (playlist) => {
    setThisDayTimeCode(dayTimeCode());
    setSelectedPlaylist(playlist);
    setShowShareModal(true);
  };

  const doEditPlaylist = async (playlist) => {
    setEditSaving(false);
    const full = await getPlaylistById(playlist.id);
    const pl = full ?? playlist;
    setEditingPlaylist(pl);
    setEditForm({
      description: pl.description ?? "",
      comments: pl.comments ?? "",
      tags: pl.tags ?? "",
    });
    setShowEditModal(true);
  };

  const doSaveEdit = async () => {
    if (!editingPlaylist) return;
    setEditSaving(true);
    try {
      await storePlaylist({
        ...editingPlaylist,
        description: editForm.description,
        comments: editForm.comments,
        tags: editForm.tags,
        shareUsers: editingPlaylist.shareUsers ?? "",
      });
      setShowEditModal(false);
      doInit();
    } catch (e) {
      console.log("Failed to save playlist", e);
    } finally {
      setEditSaving(false);
    }
  };

  const doDoShare = async (share) => {
    setShowShareModal(false);
    if (!share) {
      return;
    }
    selectedPlaylist.shareStatus = share.shareStatus;
    selectedPlaylist.shareUsers = share.shareUsers;
    await sharePlaylist(selectedPlaylist);
    if (share.notifyByEmail && share.shareStatus === 2) {
      const recipients = Array.isArray(share.shareUsers)
        ? share.shareUsers
        : String(share.shareUsers ?? "").split(",");
      sendShareNotification({
        to: recipients,
        itemType: "playlist",
        itemTitle: selectedPlaylist.description,
        sharedByEmail: userEmail,
      });
    }
    forceUpdate((n) => !n);
  };

  const getPlaylistById = async (playlistId) => {
    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: process.env.REACT_APP_VBLIVE_API_URL.includes("vercel")
        ? process.env.REACT_APP_VBLIVE_API_URL +
          `/Session/GetPlaylistById/${playlistId}`
        : process.env.REACT_APP_VBLIVE_API_URL +
          `/Session/GetPlaylistById?playlistId=${playlistId}`,
      headers: {},
    };

    var pl = null;
    await axios
      .request(config)
      .then((response) => {
        // console.log(JSON.stringify(response.data));
        pl = response.data[0];
        pl.description = decodeHtml(pl.description);
        pl.comments = decodeHtml(pl.comments);
        pl.tags = decodeHtml(pl.tags);
      })
      .catch((error) => {
        console.log(error);
      });
    return pl;
  };

  const doPlaylist = async (playlist) => {
    const pl = await getPlaylistById(playlist.id);
    const st = {
      playlist: pl,
      serverName: userEmail,
    };
    navigate("/playlist", { state: st });
  };

  const doSort = (aschs, sc, sa) => {
    var xx = null;
    if (sc === 0) {
      xx = sa
        ? aschs.sort((a, b) => a.serverName.localeCompare(b.serverName))
        : aschs.sort((a, b) => b.serverName.localeCompare(a.serverName));
    } else if (sc === 1) {
      xx = sa
        ? aschs.sort((a, b) => a.description.localeCompare(b.description))
        : aschs.sort((a, b) => b.description.localeCompare(a.description));
    } else if (sc === 2) {
      xx = sa
        ? aschs.sort((a, b) => a.dateInSeconds - b.dateInSeconds)
        : aschs.sort((a, b) => b.dateInSeconds - a.dateInSeconds);
    } else {
      return;
    }

    setFilteredPlaylists(xx);
    setSortColumn(sc);
    setSortAscending(sa);
    forceUpdate((n) => !n);
  };

  const columnHeader = (col, name) => {
    return (
      <th
        onClick={() => {
          var asc = sortColumn === col ? !sortAscending : true;
          doSort(filteredPlaylists, col, asc);
        }}
        scope="col"
        className="table-header-column text-sm p-2"
      >
        <div className="flex">
          <p>{t(name)}</p>
          {sortColumn === col ? (
            <div>
              {sortAscending ? (
                <ArrowDownIcon
                  className="ml-1 mt-0.5 h-4 w-4 flex-shrink-0"
                  aria-hidden="true"
                />
              ) : (
                <ArrowUpIcon
                  className="ml-1 mt-0.5 h-4 w-4 flex-shrink-0"
                  aria-hidden="true"
                />
              )}
            </div>
          ) : (
            <></>
          )}
        </div>
      </th>
    );
  };

  const getSharedColour = (playlist) => {
    if (playlist) {
      if (playlist.shareStatus === undefined || playlist.shareStatus === null) {
        return "mr-1.5 h-5 w-5 flex-shrink-0 text-error";
      } else {
        if (playlist.shareStatus === 1) {
          return "mr-1.5 h-5 w-5 flex-shrink-0 text-success";
        } else if (playlist.shareStatus === 2) {
          return "mr-1.5 h-5 w-5 flex-shrink-0 text-warning";
        } else {
          return "mr-1.5 h-5 w-5 flex-shrink-0 text-error";
        }
      }
    } else {
      return "mr-1.5 h-5 w-5 flex-shrink-0 text-error";
    }
  };

  const onPlaylistsScreenChanged = async (screen) => {
    setSelectedScreen(screen);
    var ms =
      screen === 0
        ? await fetchAllMyPlaylists()
        : await fetchAllSharedPlaylists();
    setAllPlaylists(ms);
    setFilteredPlaylists(ms);
    setSelectedTagOptions([{ value: "All", label: t("All Tags") }]);
  };

  const doInit = async () => {
    var ms =
      selectedScreen === 0
        ? await fetchAllMyPlaylists()
        : await fetchAllSharedPlaylists();
    var tags = [];
    for (var m of ms) {
      const pltags = m.tags.split("|");
      for (var pltag of pltags) {
        if (tags.filter((t) => t.value === pltag).length === 0) {
          tags.push({ value: pltag, label: pltag });
        }
      }
    }
    const aps = tags.sort((a, b) => a.label.localeCompare(b.label));
    var apops = [{ value: "All", label: t("All Tags") }];
    for (var p of aps) {
      apops.push(p);
    }
    setAllTagsOptions(apops);
    doSort(ms, sortColumn, sortAscending);
    setAllPlaylists(ms);
    setFilteredPlaylists(ms);
  };

  useEffect(() => {
    if (allPlaylists.length === 0) {
      doInit();
    }
  }, []);

  const tabs = [
    { name: "My Playlists", index: 0, current: true },
    { name: "Shared Playlists", index: 1, current: false },
  ];

  return (
    <>
      <div className="flex-col h-[88vh] mt-4">
        {loading ? (
          <Spinner />
        ) : (
          <>
            <div className="flex-col">
              <div className="border-b border-gray-200">
                <nav aria-label="Tabs" className="-mb-px flex space-x-8">
                  {tabs.map((tab) => (
                    <a
                      key={tab.name}
                      onClick={() => onPlaylistsScreenChanged(tab.index)}
                      aria-current={
                        tab.index === selectedScreen ? "page" : undefined
                      }
                      className={classNames(
                        tab.index === selectedScreen
                          ? "border-primary/80 text-primary/80"
                          : "border-transparent text-base-content hover:border-base-content/30 hover:text-base-content/70",
                        "whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium"
                      )}
                    >
                      {t(tab.name)}
                    </a>
                  ))}
                </nav>
              </div>

              <div className="flex gap-2 my-4">
                <div className="flex gap-2 w-72 -mt-0.5">
                  <div className="mt-1.5 w-16 text-sm text-base-content">{t("Tags:")}</div>
                  <Select
                    value={selectedTagOptions}
                    options={allTagsOptions}
                    onChange={onTagChanged}
                    placeholder={t("Select...")}
                    className="w-72 h-[10px] text-sm"
                    isMulti
                  />
                </div>
                {selectedScreen === 1 && (
                  <label className="flex items-center gap-2 ml-4 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-sm rounded-none"
                      checked={showPublic}
                      onChange={(e) => setShowPublic(e.target.checked)}
                    />
                    {t("Show public playlists")}
                  </label>
                )}
              </div>

              <div className="overflow-x-auto">
                <div className="inline-block min-w-full py-2 align-middle">
                  <div className="">
                    <table className="table-generic">
                      <thead className="thead-generic">
                        <tr>
                          {/* <th
                            scope="col"
                            className="px-3 py-2 text-left text-sm font-semibold text-base-content"
                          ></th> */}
                          {columnHeader(0, "Owner")}
                          {columnHeader(1, "Description")}
                          {columnHeader(2, "Date")}
                          {selectedScreen === 0 ? (
                            <>
                              <th
                                scope="col"
                                className="px-3 py-2 text-left text-sm font-semibold text-base-content"
                              ></th>
                              <th
                                scope="col"
                                className="px-3 py-2 text-left text-sm font-semibold text-base-content"
                              ></th>
                              <th
                                scope="col"
                                className="px-3 py-2 text-left text-sm font-semibold text-base-content"
                              ></th>
                            </>
                          ) : (
                            <></>
                          )}
                          <th
                            scope="col"
                            className="px-3 py-2 text-left text-sm font-semibold text-base-content"
                          ></th>
                        </tr>
                      </thead>
                      <tbody className="tbody-generic">
                        {filteredPlaylists &&
                          filteredPlaylists
                          .filter((pl) => selectedScreen !== 1 || showPublic || pl.shareStatus !== 1)
                          .map((playlist, i) => (
                            <tr
                              key={i}
                              className={
                                i % 2 === 0
                                  ? "bg-transparent"
                                  : "bg-base-300/10"
                              }
                            >
                              {/* <td className="table-cell">
                                <input
                                  type="checkbox"
                                  checked={
                                    playlist.selected &&
                                    playlist.selected === true
                                  }
                                  className="checkbox checkbox-sm mt-1 rounded-none"
                                  onChange={() => doToggleSelect(playlist)}
                                />
                              </td> */}
                              <td className="table-cell">
                                <div className="flex gap-1">
                                  <div className="">{playlist.serverName}</div>
                                </div>
                              </td>
                              <td className="table-cell">
                                <div className="flex gap-1">
                                  <div
                                    className="cursor-pointer underline hover:text-primary"
                                    onClick={() => doPlaylist(playlist)}
                                  >
                                    {playlist.description}
                                  </div>
                                </div>
                              </td>
                              <td className="table-cell">
                                {new Date(
                                  playlist.dateInSeconds * 1000
                                ).toLocaleDateString()}
                              </td>
                              {selectedScreen === 0 ? (
                                <>
                                  <td className="table-cell">
                                    <p className="cursor-pointer flex items-center text-sm text-gray-500">
                                      <PencilIcon
                                        className="mr-1.5 h-5 w-5 flex-shrink-0"
                                        aria-hidden="true"
                                        onClick={() => doEditPlaylist(playlist)}
                                      />
                                    </p>
                                  </td>
                                  <td className="table-cell">
                                    <p className="cursor-pointer flex items-center text-sm text-gray-500">
                                      <TrashIcon
                                        className="mr-1.5 h-5 w-5 flex-shrink-0"
                                        aria-hidden="true"
                                        onClick={() =>
                                          doDeletePlaylist(playlist)
                                        }
                                      />
                                    </p>
                                  </td>
                                  <td className="table-cell">
                                    <p className="cursor-pointer flex items-center text-sm text-gray-500">
                                      <ShareIcon
                                        className={getSharedColour(playlist)}
                                        aria-hidden="true"
                                        onClick={() =>
                                          doSharePlaylist(playlist)
                                        }
                                      />
                                    </p>
                                  </td>
                                </>
                              ) : (
                                <></>
                              )}
                              <td className="table-cell">
                                <p className="cursor-pointer flex items-center text-sm text-gray-500">
                                  <VideoCameraIcon
                                    className="mr-1.5 h-5 w-5 flex-shrink-0"
                                    aria-hidden="true"
                                    onClick={() => doPlaylist(playlist)}
                                  />
                                </p>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {showEditModal && editingPlaylist && (
        <dialog
          className="modal z-[9999]"
          open
          onKeyDown={(e) => {
            if (e.key === "Escape") setShowEditModal(false);
          }}
        >
          <div className="modal-box sm:w-5/12 w-full max-w-2xl min-w-[480px] max-h-[85vh] overflow-y-auto shadow rounded-none">
            <h3 className="mb-4 font-bold text-2xl">{t("Edit playlist")}</h3>
            <div className="flex flex-col gap-3">
              <div>
                <p className="mb-0.5 text-xs font-semibold text-base-content/60">
                  {t("Description")}
                </p>
                <input
                  type="text"
                  className="input-generic"
                  value={editForm.description}
                  onChange={(e) =>
                    setEditForm({ ...editForm, description: e.target.value })
                  }
                />
              </div>
              <div>
                <p className="mb-0.5 text-xs font-semibold text-base-content/60">
                  {t("Comments")}
                </p>
                <textarea
                  className="textarea textarea-bordered w-full text-sm"
                  rows={3}
                  value={editForm.comments}
                  onChange={(e) =>
                    setEditForm({ ...editForm, comments: e.target.value })
                  }
                />
              </div>
              <div>
                <p className="mb-0.5 text-xs font-semibold text-base-content/60">
                  {t("Tags")}
                </p>
                <input
                  type="text"
                  className="input-generic"
                  value={editForm.tags}
                  onChange={(e) =>
                    setEditForm({ ...editForm, tags: e.target.value })
                  }
                />
              </div>
              {usingFirestore() && (
                <PlaylistAssignmentsPanel
                  playlist={{ ...editingPlaylist, ...editForm }}
                  t={t}
                />
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                className="btn btn-sm btn-ghost"
                onClick={() => setShowEditModal(false)}
              >
                {t("Cancel")}
              </button>
              <button
                className="btn btn-sm btn-primary"
                onClick={doSaveEdit}
                disabled={editSaving}
              >
                {editSaving ? t("Saving...") : t("Save")}
              </button>
            </div>
          </div>
          <form
            method="dialog"
            className="modal-backdrop"
            onClick={() => setShowEditModal(false)}
          />
        </dialog>
      )}

      {showShareModal && (
        <dialog className="modal z-[9999]" open onKeyDown={(e) => { if (e.key === "Escape") setShowShareModal(false); }}>
          <div className="modal-box sm:w-4/12 w-full max-w-5xl min-w-[480px] h-[70vh] shadow rounded-none">
            <h3 className="mb-4 font-bold text-2xl">{t("Share")}</h3>
            <div className="flex flex-col">
              <div>
                <Share
                  shareStatus={selectedPlaylist && selectedPlaylist.shareStatus}
                  shareUsers={selectedPlaylist && selectedPlaylist.shareUsers}
                  currentTime={thisDayTimeCode}
                  onShare={(share) => doDoShare(share)}
                />
              </div>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop" onClick={() => setShowShareModal(false)} />
        </dialog>
      )}
    </>
  );
}

export default PlaylistsList;
